/**
 * How a canvas item takes the light, per `canvas.glsl`: `color.rgb = albedo × S`. `useCanvasItemTint`
 * composes `albedo × canvas_modulation`, so the injection divides the tint out. A non-HDR 2D
 * viewport (`hdr_2d` false) never goes linear, so the injection decodes to sRGB, does Godot's
 * arithmetic with its [0, 1] clamp, and re-encodes before three's `colorspace_fragment`.
 */
/*
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 */
import { editedProgram } from '../shaderPatch/chunkPatch.js';
import { GODOT_TO_LINEAR_GLSL, GODOT_TO_SRGB_GLSL } from '../../godot/rendering/srgbTransfer.js';
import { CanvasItemMaterialLightMode } from '../../godot/scene/canvasItemMaterial.js';
/**
 * The floor of the canvas modulate the CPU folds in and the shader divides by, so the divide-out
 * is exact. Without it a black CanvasModulate, the usual night, erases the albedo. One 8-bit step
 * costs at most that on the base term and nothing lit, since `S` carries the true modulate.
 */
export const CANVAS_MODULATE_FLOOR = 1 / 255;
/** Both directions: the item side encodes to compare and decodes to write back. */
const TRANSFER_GLSL = GODOT_TO_SRGB_GLSL + GODOT_TO_LINEAR_GLSL;
/** The uniforms every lit item's program reads, with the sRGB transfer it shades in. */
export const ITEM_LIGHTING_GLSL = `uniform vec2 uLightResolution;
uniform vec3 uCanvasModulate;
uniform float uLightMode;
${TRANSFER_GLSL}`;
/** Both injections depend only on module constants, so they are built once. */
const UNIFORM_PREAMBLE = `uniform sampler2D uLightList;
uniform sampler2D uShadowTint;
uniform float uLit;
${ITEM_LIGHTING_GLSL}
void main() {`;
/** Midpoints between adjacent LightMode ordinals, so the GLSL never tests equality. */
const midpoint = (a, b) => ((a + b) / 2).toFixed(1);
const ABOVE_NORMAL = midpoint(CanvasItemMaterialLightMode.NORMAL, CanvasItemMaterialLightMode.UNSHADED);
const ABOVE_UNSHADED = midpoint(CanvasItemMaterialLightMode.UNSHADED, CanvasItemMaterialLightMode.LIGHT_ONLY);
/** The item's light mode, from `uLightMode`, as every lit item's program reads it. */
export const LIGHT_MODE_GLSL = `bool unshaded = uLightMode > ${ABOVE_NORMAL} && uLightMode < ${ABOVE_UNSHADED};
bool lightOnly = uLightMode > ${ABOVE_UNSHADED};`;
/**
 * Where `S` starts, the fragment's place in the light buffers, and the albedo. canvas.glsl:713:
 * Light Only skips the tint, and its buffers are seeded to match. Dividing by the seed is the tint
 * divide-out: Light Only seeds at 1, where the fragment already is the albedo.
 */
export const ITEM_ALBEDO_GLSL = `vec3 lightSeed = lightOnly ? vec3(1.0) : uCanvasModulate;
vec2 lightUv = gl_FragCoord.xy / uLightResolution;
vec3 albedo = godotToSrgb(gl_FragColor.rgb) / max(lightSeed, vec3(${CANVAS_MODULATE_FLOOR}));`;
const LIGHT_INJECTION = `${LIGHT_MODE_GLSL}
// canvas.glsl:719: MODE_UNSHADED skips the canvas tint and the light loop,
// so the fragment leaves as it arrived. So does an item no light reaches: the CPU already folded
// the tint in, and the round trip through the two sRGB curves would only perturb it.
if (!unshaded && (uLit > 0.5 || lightOnly)) {
  ${ITEM_ALBEDO_GLSL}
  vec4 accum = uLit > 0.5 ? texture2D(uLightList, lightUv) : vec4(lightSeed, 0.0);
  // shadow_color is the one light term Godot does NOT scale by the albedo.
  vec3 shadowTint = texture2D(uShadowTint, lightUv).rgb;
  if (lightOnly) gl_FragColor.a = clamp(gl_FragColor.a * accum.a, 0.0, 1.0);
  gl_FragColor.rgb = godotToLinear(clamp(albedo * accum.rgb + shadowTint, 0.0, 1.0));
}
#include <colorspace_fragment>`;
/** Constant: the injected source is the same literal for every item. */
const PROGRAM_CACHE_KEY = 'godot-canvas-light';
/**
 * Props that make a `meshBasicMaterial` sample the light accumulators, as a merge part for
 * `materialProgramInputs()`. An item that passes none stays unlit, as every 3D consumer needs. The
 * props are the same for every light mode, with or without lights: both are uniforms.
 */
export function canvasItemLightingProps(uniforms) {
    return {
        transparent: true,
        injection: {
            cacheKey: PROGRAM_CACHE_KEY,
            onBeforeCompile: (shader) => {
                shader.uniforms.uLightList = uniforms.lightBuffer;
                shader.uniforms.uShadowTint = uniforms.shadowTintBuffer;
                shader.uniforms.uLit = uniforms.isLit;
                shader.uniforms.uLightResolution = uniforms.resolution;
                shader.uniforms.uCanvasModulate = uniforms.canvasModulate;
                shader.uniforms.uLightMode = uniforms.lightMode;
                editedProgram(shader, {
                    fragment: [
                        { three: 'void main() {', godot: UNIFORM_PREAMBLE },
                        { three: '#include <colorspace_fragment>', godot: LIGHT_INJECTION },
                    ],
                }, 'canvas item lighting');
            },
        },
    };
}
//# sourceMappingURL=canvasItemLighting.js.map